/**
 * ============================================================================
 * INSPECTIONS HOOK - useInspections
 * ============================================================================
 * Centralized inspection data management
 * Version: v3.10.0-CLEAR-MAP
 * 
 * This hook manages:
 * - Inspection list (CRUD operations)
 * - Due inspections tracking
 * - Edit mode state
 * - Focused edit navigation
 * - Data table pagination
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import * as api from '../services/apiService';
import { extractAdditionalNotes } from '../utils/inspectionHelper';
import { formatDate } from '../utils/dateHelper';

// Create Inspections Context
const InspectionsContext = createContext(null);

// Inspections Provider Component
export const InspectionsProvider = ({ children }) => {
  const { user, submittedOnBehalfOf } = useAuth();
  
  // ============================================================================
  // STATE
  // ============================================================================
  
  // Inspection data
  const [inspections, setInspections] = useState([]);
  const [dueInspections, setDueInspections] = useState([]);
  
  // Edit mode state
  const [editingInspection, setEditingInspection] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Focused edit mode state
  const [focusedEditMode, setFocusedEditMode] = useState(false);
  const [currentInspectionIndex, setCurrentInspectionIndex] = useState(0);
  const [modalImage, setModalImage] = useState(null);
  const [pendingOpenInspectionId, setPendingOpenInspectionId] = useState(null);
  
  // Data table pagination
  const [dataTablePageSize, setDataTablePageSize] = useState(20);
  const [dataTableCurrentPage, setDataTableCurrentPage] = useState(1);
  
  // ============================================================================
  // API WRAPPER FUNCTIONS
  // ============================================================================
  
  /**
   * Load all inspections from backend
   * 🔧 v61 FIX: Load ALL inspections (limit=1000) for accurate leaderboard
   */
  const loadInspections = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await api.loadInspections(1000, 0);  // Load all for leaderboard accuracy
      
      // Handle both old format (array) and new format (wrapped object)
      const data = Array.isArray(response) ? response : (response.inspections || []);
      
      console.log('📊 Loaded inspections data:', data.length, 'inspections');
      
      setInspections(data);
    } catch (error) {
      console.error('Failed to load inspections:', error);
    }
  };

  /**
   * Load due inspections from backend
   */
  const loadDueInspections = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const data = await api.loadDueInspections();
      setDueInspections(data);
    } catch (error) {
      console.error('Failed to load due inspections:', error);
    }
  };

  /**
   * Refresh all inspection data
   */
  const refreshInspections = async () => {
    await loadInspections();
    await loadDueInspections();
  };

  /**
   * Save inspection changes
   */
  const saveInspectionChanges = async (editFormData, showToast) => {
    if (!editingInspection) return;

    try {
      // 🔧 FIX: Format data correctly for backend InspectionUpdate model
      const formattedData = { ...editFormData };
      
      // 🚨 CRITICAL: Ensure working fields are included
      
      // Convert date strings to backend expected format
      if (editFormData.last_inspection_date) {
        const date = new Date(editFormData.last_inspection_date);
        formattedData.last_inspection_date = {
          year: date.getFullYear(),
          month: date.getMonth() + 1, // JavaScript months are 0-indexed
          day: date.getDate(),
          extracted_text: editFormData.last_inspection_date
        };
      }
      
      if (editFormData.next_due_date) {
        const date = new Date(editFormData.next_due_date);
        formattedData.next_due_date = {
          year: date.getFullYear(),
          month: date.getMonth() + 1, // JavaScript months are 0-indexed
          day: date.getDate(),
          extracted_text: editFormData.next_due_date
        };
      }
      
      await api.saveInspectionChanges(editingInspection, formattedData);
      if (showToast) showToast('✅ Changes saved successfully!', 'success');
      setEditingInspection(null);
      setEditFormData({});
      await refreshInspections();
    } catch (error) {
      console.error('❌ Save failed:', error);
      if (showToast) showToast('❌ Failed to save changes. Please try again.', 'error');
    }
  };

  /**
   * Delete inspection
   */
  const deleteInspection = async (inspectionId, showToast) => {
    if (!window.confirm('Are you sure you want to delete this inspection? This action cannot be undone.')) {
      return;
    }

    try {
      await api.deleteInspection(inspectionId);
      if (showToast) showToast('✅ Inspection deleted successfully!', 'success');
      await refreshInspections();
      
      // If we were editing this inspection, cancel editing
      if (editingInspection === inspectionId) {
        setEditingInspection(null);
        setEditFormData({});
      }
    } catch (error) {
      console.error('Delete failed:', error);
      if (showToast) showToast('❌ Failed to delete inspection. Please try again.', 'error');
    }
  };

  /**
   * Approve inspection (with auto-save)
   */
  const approveInspection = async (inspectionId, editFormData, currentUser, showToast) => {
    console.log('🚀 APPROVE BUTTON CLICKED:', inspectionId);
    
    // Show loading toast
    if (showToast) showToast('⏳ Processing approval...', 'info');
    
    try {
      const inspection = inspections.find(i => i.id === inspectionId);
      
      if (!inspection) {
        throw new Error('Inspection not found');
      }
      
      const currentApprovalStatus = inspection?.approval_status || 'pending';
      console.log('📋 Current approval status:', currentApprovalStatus);
      
      if (currentApprovalStatus === 'approved') {
        // Reverting approval - just toggle status
        console.log(`🔄 Reverting approval for ${inspectionId}`);
        
        const response = await api.approveInspection(inspectionId, {
          approver_name: currentUser || 'Admin User',
          approval_status: 'pending'
        });
        
        setInspections(prev => prev.map(insp => 
          insp.id === inspectionId 
            ? { 
                ...insp, 
                approval_status: 'pending',
                approved_by: null,
                approval_timestamp: null,
                human_review_status: 'pending',
                reviewed_by: null,
                review_timestamp: null
              }
            : insp
        ));
        if (showToast) showToast('🔄 Approval reverted successfully', 'info');
        
      } else {
        // Approving - Auto-save changes first, then approve
        console.log(`✅ Auto-saving changes and approving ${inspectionId}`);
        
        // 💾 STEP 1: Auto-save any pending changes 
        if (editingInspection === inspectionId && Object.keys(editFormData).length > 0) {
          console.log('💾 Auto-saving form changes before approval...');
          
          try {
            // Format data correctly for backend - only send valid InspectionUpdate fields
            const formattedData = {};
            
            // Only include fields that the InspectionUpdate model accepts
            const validFields = ['last_inspection_date', 'next_due_date', 'extinguisher_type', 'maintenance_notes', 'condition', 'requires_attention', 'location', 'notes'];
            
            for (const field of validFields) {
              if (editFormData[field] !== undefined) {
                formattedData[field] = editFormData[field];
              }
            }
            
            // Convert date fields to the expected format
            if (formattedData.last_inspection_date) {
              const dateValue = formattedData.last_inspection_date;
              
              if (typeof dateValue === 'string') {
                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  formattedData.last_inspection_date = {
                    year: date.getFullYear(),
                    month: date.getMonth() + 1,
                    day: date.getDate(),
                    extracted_text: dateValue
                  };
                } else {
                  console.error('💾 Invalid last_inspection_date string');
                  delete formattedData.last_inspection_date;
                }
              } else if (typeof dateValue === 'object' && dateValue.year) {
                // Already in correct format, keep as is
              } else {
                console.error('💾 Unexpected last_inspection_date format');
                delete formattedData.last_inspection_date;
              }
            }
            
            if (formattedData.next_due_date) {
              const dateValue = formattedData.next_due_date;
              
              if (typeof dateValue === 'string') {
                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  formattedData.next_due_date = {
                    year: date.getFullYear(),
                    month: date.getMonth() + 1,
                    day: date.getDate(),
                    extracted_text: dateValue
                  };
                } else {
                  console.error('💾 Invalid next_due_date string');
                  delete formattedData.next_due_date;
                }
              } else if (typeof dateValue === 'object' && dateValue.year) {
                // Already in correct format, keep as is
              } else {
                console.error('💾 Unexpected next_due_date format');
                delete formattedData.next_due_date;
              }
            }
            
            // Clean up any null/undefined values
            Object.keys(formattedData).forEach(key => {
              if (formattedData[key] === null || formattedData[key] === undefined || formattedData[key] === '') {
                delete formattedData[key];
              }
            });
            
            // Save changes first
            await api.saveInspectionChanges(inspectionId, formattedData);
            console.log('✅ Changes auto-saved successfully');
          } catch (saveError) {
            console.error('❌ Error saving changes:', saveError);
            if (showToast) showToast('⚠️ Failed to save changes, but continuing with approval', 'warning');
          }
        }
        
        // 🎯 STEP 2: Approve the inspection
        console.log('🔗 Making approval API call');
        const approvalResponse = await api.approveInspection(inspectionId, {
          approver_name: currentUser || 'Admin User',
          approval_status: 'approved'
        });
        console.log('📡 Approval API response received');
        
        // Update local state and close edit interface
        setInspections(prev => prev.map(insp => 
          insp.id === inspectionId 
            ? { 
                ...insp, 
                approval_status: 'approved',
                approved_by: currentUser || 'Admin User',
                approval_timestamp: new Date().toISOString(),
                human_review_status: 'confirmed',
                reviewed_by: currentUser || 'Admin User',
                review_timestamp: new Date().toISOString()
              }
            : insp
        ));
        
        // Close edit interface and clear form
        setEditingInspection(null);
        setEditFormData({});
        
        // Refresh data to ensure consistency
        console.log('🔄 Refreshing inspection data after approval...');
        
        // Wait a moment for the database to be updated
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Force a complete refresh of the inspections data
        await loadInspections();
        console.log('✅ Data refresh completed');
        
        if (showToast) showToast('✅ Inspection approved successfully!', 'success');
      }
      
    } catch (error) {
      console.error('❌ Error during approval process:', error);
      const errorMessage = error.message || 'Failed to complete approval process';
      if (showToast) showToast(`❌ ${errorMessage}`, 'error');
      throw error; // Re-throw to allow caller to handle if needed
    }
  };

  /**
   * Toggle human review status
   */
  const toggleHumanReview = async (inspectionId, currentUser, showToast) => {
    try {
      const inspection = inspections.find(i => i.id === inspectionId);
      const currentStatus = inspection?.human_review_status || 'pending';
      const newStatus = currentStatus === 'confirmed' ? 'pending' : 'confirmed';
      
      console.log(`🔍 Toggling review status for ${inspectionId}: ${currentStatus} → ${newStatus}`);
      
      const result = await api.toggleHumanReview(inspectionId, newStatus);
      console.log('✅ Review status updated');
      
      // Update local state
      setInspections(prev => prev.map(insp => 
        insp.id === inspectionId 
          ? { 
              ...insp, 
              human_review_status: newStatus,
              reviewed_by: currentUser || 'Admin User',
              review_timestamp: new Date().toISOString()
            }
          : insp
      ));
      
      const statusText = newStatus === 'confirmed' ? 'APPROVED' : 'REVIEW PENDING';
      const emoji = newStatus === 'confirmed' ? '✅' : '🔍';
      if (showToast) showToast(`${emoji} Human review ${statusText}`, 'success');
    } catch (error) {
      console.error('Error updating review status:', error);
      if (showToast) showToast('❌ Failed to update review status', 'error');
    }
  };

  // ============================================================================
  // EDIT MODE FUNCTIONS
  // ============================================================================
  
  /**
   * Start editing an inspection
   */
  const startEditingInspection = (inspection) => {
    setEditingInspection(inspection.id);
    
    // ### ENABLE FOCUSED EDIT MODE ###
    setFocusedEditMode(true);
    
    // Find current inspection index for navigation
    const currentIndex = inspections.findIndex(insp => insp.id === inspection.id);
    setCurrentInspectionIndex(currentIndex >= 0 ? currentIndex : 0);

    let parsedData = {};
    try {
        const responseText = typeof inspection.gemini_response === 'string'
            ? inspection.gemini_response
            : JSON.stringify(inspection.gemini_response);

        const jsonMatch = responseText.match(/```json\s*(\{[\s\S]*?\})\s*```|(\{[\s\S]*\})/);

        if (jsonMatch) {
            const jsonString = jsonMatch[1] || jsonMatch[2];
            parsedData = JSON.parse(jsonString);
        } else {
            parsedData = {};
        }
    } catch (e) {
        console.error('Could not parse gemini response:', e);
        parsedData = {};
    }

    
    // DIRECT FIX: Use the AI extracted dates exactly as they are
    let formattedLastDate = '';
    let formattedNextDate = '';
    
    // Use AI extracted dates directly - with null checking
    if (parsedData.last_inspection_date && parsedData.last_inspection_date.year && parsedData.last_inspection_date.month && parsedData.last_inspection_date.day) {
      const { year, month, day } = parsedData.last_inspection_date;
      formattedLastDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      console.log('🔍 Using AI date:', formattedLastDate);
    } else {
      // Use today if AI date is incomplete (null day, etc.)
      formattedLastDate = new Date().toISOString().split('T')[0];
      console.log('🔍 Using today as fallback (AI date incomplete):', formattedLastDate);
    }
    
    // Calculate next due date as exactly 1 year later
    if (formattedLastDate) {
      const lastDate = new Date(formattedLastDate);
      const nextDate = new Date(lastDate);
      nextDate.setFullYear(lastDate.getFullYear() + 1);
      formattedNextDate = nextDate.toISOString().split('T')[0];
    }
    
    // Extract additional notes using the helper function
    const additionalNotes = extractAdditionalNotes(inspection);
    
    setEditFormData({
        location: inspection.location || '',
        notes: additionalNotes, // Use extracted additional notes instead of full notes field
        last_inspection_date: formattedLastDate,
        next_due_date: formattedNextDate,
        extinguisher_type: parsedData.extinguisher_type || '',
        test_extinguisher_type: parsedData.test_extinguisher_type || parsedData.service_company?.name || '', // NOW PULLS SERVICE COMPANY!
        maintenance_notes: parsedData.maintenance_notes || '',
        condition: parsedData.condition || '',
        requires_attention: parsedData.requires_attention || false,
        // WORKING PATTERN: Use direct fields like extinguisher_type (PRIORITIZE SCAN DATA, THEN SAVED VALUES)
        working_service_company_name: parsedData.service_company?.name || parsedData.working_service_company_name || inspection.service_company?.name || '',
        working_service_company_phone: parsedData.service_company?.phone || parsedData.working_service_company_phone || inspection.service_company?.phone || '',
        working_additional_notes: parsedData.working_additional_notes || inspection.additional_notes || inspection.notes || '',
        // Enhanced fields - BROKEN (keeping for reference)
        service_company_name: inspection.service_company?.name || parsedData.service_company?.name || '',
        service_company_phone: inspection.service_company?.phone || parsedData.service_company?.phone || '',
        service_company_address: inspection.service_company?.address || parsedData.service_company?.address || '',
        service_company_website: inspection.service_company?.website || parsedData.service_company?.website || '',
        ae_number: parsedData.equipment_numbers?.ae_number || '',
        he_number: parsedData.equipment_numbers?.he_number || '',
        ee_number: parsedData.equipment_numbers?.ee_number || '',
        fe_number: parsedData.equipment_numbers?.fe_number || '',
        service_type: parsedData.service_details?.service_type || '',
    });
  };

  /**
   * Cancel editing
   */
  const cancelEditing = () => {
    setEditingInspection(null);
    setEditFormData({});
    
    // ### EXIT FOCUSED MODE ###
    setFocusedEditMode(false);
  };

  /**
   * Navigate to next inspection
   */
  const navigateToNextInspection = () => {
    if (currentInspectionIndex < inspections.length - 1) {
      const nextInspection = inspections[currentInspectionIndex + 1];
      setCurrentInspectionIndex(currentInspectionIndex + 1);
      startEditingInspection(nextInspection);
    }
  };

  /**
   * Navigate to previous inspection
   */
  const navigateToPrevInspection = () => {
    if (currentInspectionIndex > 0) {
      const prevInspection = inspections[currentInspectionIndex - 1];
      setCurrentInspectionIndex(currentInspectionIndex - 1);
      startEditingInspection(prevInspection);
    }
  };

  /**
   * Exit focused edit mode
   */
  const exitFocusedMode = () => {
    setFocusedEditMode(false);
    setEditingInspection(null);
    setEditFormData({});
  };

  // ============================================================================
  // DATA TABLE PAGINATION
  // ============================================================================
  
  /**
   * Get paginated data table data
   */
  const getPaginatedInspections = () => {
    // Safety check: ensure inspections is an array
    const safeInspections = Array.isArray(inspections) ? inspections : [];
    
    const startIndex = (dataTableCurrentPage - 1) * dataTablePageSize;
    const endIndex = startIndex + dataTablePageSize;
    const paginatedData = safeInspections.slice(startIndex, endIndex);
    
    return {
      data: paginatedData,
      totalPages: Math.ceil(safeInspections.length / dataTablePageSize),
      currentPage: dataTableCurrentPage,
      totalItems: safeInspections.length,
      startIndex: startIndex + 1,
      endIndex: Math.min(endIndex, safeInspections.length)
    };
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  /**
   * Load inspections when user is authenticated
   */
  useEffect(() => {
    // Load when MSAL user is authenticated OR when onboarding email has been set
    if (user || submittedOnBehalfOf) {
      loadInspections();
      loadDueInspections();
    }
  }, [user, submittedOnBehalfOf]);
  
  /**
   * 🔧 FIX: Listen for submission-completed to refresh inspections & points immediately
   */
  useEffect(() => {
    const handleSubmissionCompleted = (e) => {
      console.log('🔄 INSPECTIONS-REFRESH: submission-completed event received, refreshing data...');
      // Small delay to ensure backend has processed
      setTimeout(() => {
        loadInspections();
        loadDueInspections();
        console.log('🔄 INSPECTIONS-REFRESH: Data refreshed!');
      }, 500);
    };
    
    window.addEventListener('submission-completed', handleSubmissionCompleted);
    console.log('🔄 INSPECTIONS-REFRESH: Event listener attached');
    
    return () => {
      window.removeEventListener('submission-completed', handleSubmissionCompleted);
    };
  }, []);

  /**
   * Open pending inspection when data loads
   */
  useEffect(() => {
    if (!pendingOpenInspectionId || inspections.length === 0) return;
    const found = inspections.find(i => i.id === pendingOpenInspectionId);
    if (found) {
      startEditingInspection(found);
      setPendingOpenInspectionId(null);
    }
  }, [pendingOpenInspectionId, inspections]);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================
  
  const value = {
    // State
    inspections,
    setInspections,
    dueInspections,
    setDueInspections,
    editingInspection,
    setEditingInspection,
    editFormData,
    setEditFormData,
    focusedEditMode,
    setFocusedEditMode,
    currentInspectionIndex,
    setCurrentInspectionIndex,
    modalImage,
    setModalImage,
    pendingOpenInspectionId,
    setPendingOpenInspectionId,
    dataTablePageSize,
    setDataTablePageSize,
    dataTableCurrentPage,
    setDataTableCurrentPage,
    
    // Functions
    loadInspections,
    loadDueInspections,
    refreshInspections,
    saveInspectionChanges,
    deleteInspection,
    approveInspection,
    toggleHumanReview,
    startEditingInspection,
    cancelEditing,
    navigateToNextInspection,
    navigateToPrevInspection,
    exitFocusedMode,
    getPaginatedInspections
  };

  return (
    <InspectionsContext.Provider value={value}>
      {children}
    </InspectionsContext.Provider>
  );
};

// ============================================================================
// HOOK
// ============================================================================

/**
 * useInspections hook - Access inspections context
 */
export const useInspections = () => {
  const context = useContext(InspectionsContext);
  if (!context) {
    throw new Error('useInspections must be used within an InspectionsProvider');
  }
  return context;
};

export default useInspections;

